import {
  afterNextRender,
  Directive,
  effect,
  ElementRef,
  inject,
  NgZone,
  OnDestroy,
  input,
  Renderer2,
} from '@angular/core';

@Directive({
  selector: '[appFitText]',
  standalone: true,
})
export class FitTextDirective implements OnDestroy {
  readonly fitTextValue = input<string | number | null>(null);
  readonly fitTextMinPx = input(24);
  readonly fitTextMaxPx = input(160);

  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly ngZone = inject(NgZone);

  private resizeObserver?: ResizeObserver;
  private mutationObserver?: MutationObserver;
  private animationFrameId?: number;
  private viewInitialized = false;

  constructor() {
    afterNextRender(() => {
      this.ngZone.runOutsideAngular(() => {
        this.viewInitialized = true;
        this.observeContainerSize();
        this.observeTextChanges();
        this.scheduleFit();
      });
    });

    effect(() => {
      this.fitTextValue();
      this.fitTextMinPx();
      this.fitTextMaxPx();

      if (this.viewInitialized) {
        this.scheduleFit();
      }
    });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();

    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
  }

  private observeContainerSize(): void {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    this.resizeObserver = new ResizeObserver(() => this.scheduleFit());
    this.resizeObserver.observe(this.getContainer());
  }

  private observeTextChanges(): void {
    if (typeof MutationObserver === 'undefined') {
      return;
    }

    this.mutationObserver = new MutationObserver(() => this.scheduleFit());
    this.mutationObserver.observe(this.elementRef.nativeElement, {
      characterData: true,
      childList: true,
      subtree: true,
    });
  }

  private scheduleFit(): void {
    const element = this.elementRef.nativeElement;
    this.renderer.removeClass(element, 'is-fit-ready');

    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }

    this.animationFrameId = requestAnimationFrame(() => this.fit());
  }

  private fit(): void {
    const element = this.elementRef.nativeElement;
    const container = this.getContainer();
    const availableWidth = container.clientWidth;
    const availableHeight = container.clientHeight;

    if (!availableWidth || !availableHeight) {
      return;
    }

    const min = Math.min(this.fitTextMinPx(), this.fitTextMaxPx());
    const max = Math.max(this.fitTextMinPx(), this.fitTextMaxPx());
    let low = min;
    let high = max;
    let best = min;

    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      this.renderer.setStyle(element, 'font-size', `${mid}px`);

      if (this.isElementInsideContainer(element, availableWidth, availableHeight)) {
        best = mid;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }

    this.renderer.setStyle(element, 'font-size', `${best}px`);
    this.renderer.addClass(element, 'is-fit-ready');
  }

  private isElementInsideContainer(
    element: HTMLElement,
    availableWidth: number,
    availableHeight: number,
  ): boolean {
    return element.scrollWidth <= availableWidth && element.scrollHeight <= availableHeight;
  }

  private getContainer(): HTMLElement {
    return this.elementRef.nativeElement.parentElement ?? this.elementRef.nativeElement;
  }
}
